import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../../../contexts/TabsContext';
import DescriptionIcon from '@mui/icons-material/Description';
import TabIcon from '@mui/icons-material/Tab';

const OfferGenerationDialog = ({ open, onClose, onGenerate }) => {
  const { t } = useTranslation();
  const { tabs } = useTabs();
  const [selectedTabs, setSelectedTabs] = useState(new Set());
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset selected tabs and client name when dialog opens
  React.useEffect(() => {
    if (open) {
      // По умолчанию выбираем все табы
      setSelectedTabs(new Set(tabs.map(tab => tab.id)));
      setClientName(''); // Reset client name
    }
  }, [open, tabs]);

  const handleTabToggle = (tabId) => {
    setSelectedTabs(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(tabId)) {
        newSelected.delete(tabId);
      } else {
        newSelected.add(tabId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectedTabs.size === tabs.length) {
      setSelectedTabs(new Set());
    } else {
      setSelectedTabs(new Set(tabs.map(tab => tab.id)));
    }
  };

  const handleGenerate = async () => {
    if (selectedTabs.size === 0 || !clientName.trim()) {
      return;
    }
    
    setLoading(true);
    try {
      const selectedTabsArray = tabs.filter(tab => selectedTabs.has(tab.id));
      const type = selectedTabsArray.length === 1 ? 'offer' : 'comparison';
      
      await onGenerate(selectedTabsArray, type, clientName.trim());
    } catch (error) {
      console.error('❌ Error in handleGenerate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setSelectedTabs(new Set());
      setClientName('');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: '#00C853' }} />
          <Typography variant="h6" component="div">
            {t('offers.generateOffer', 'Generate Offer')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('offers.selectTabsDescription', 'Select the pricing configurations you want to include in your offer:')}
        </Typography>

        {/* Client Name Field */}
        <TextField
          label={t('offers.clientName', 'Client Name *')}
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          fullWidth
          required
          sx={{ mb: 3 }}
          placeholder={t('offers.clientNamePlaceholder', 'Enter client name...')}
          error={!clientName.trim()}
          helperText={!clientName.trim() ? t('offers.clientNameRequired', 'Client name is required') : ''}
          disabled={loading}
        />

        {/* Select All Checkbox */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedTabs.size === tabs.length && tabs.length > 0}
                indeterminate={selectedTabs.size > 0 && selectedTabs.size < tabs.length}
                onChange={handleSelectAll}
                sx={{
                  color: '#00C853',
                  '&.Mui-checked': {
                    color: '#00C853',
                  },
                }}
              />
            }
            label={t('offers.selectAll', 'Select All')}
            sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Tabs List */}
        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {tabs.map((tab, index) => (
            <ListItem
              key={tab.id}
              sx={{
                px: 0,
                '&:hover': {
                  bgcolor: 'rgba(0, 200, 83, 0.04)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  checked={selectedTabs.has(tab.id)}
                  onChange={() => handleTabToggle(tab.id)}
                  sx={{
                    color: '#00C853',
                    '&.Mui-checked': {
                      color: '#00C853',
                    },
                  }}
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TabIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={tab.name}
                secondary={`${t('offers.configuration', 'Configuration')} ${index + 1}`}
                primaryTypographyProps={{
                  fontWeight: selectedTabs.has(tab.id) ? 500 : 400
                }}
              />
            </ListItem>
          ))}
        </List>

        {tabs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('offers.noTabsAvailable', 'No pricing configurations available')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={selectedTabs.size === 0 || loading || !clientName.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
          sx={{
            bgcolor: '#00C853',
            minWidth: 140,
            '&:hover': {
              bgcolor: '#00A040',
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)'
            }
          }}
        >
          {loading 
            ? t('offers.generating', 'Generating...') 
            : selectedTabs.size === 1 
              ? t('offers.generateOffer', 'Generate Offer')
              : t('offers.generateComparison', 'Generate Comparison')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferGenerationDialog;